import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"
import { useTheme } from "@/lib/theme"

const Toaster = ({ ...props }: ToasterProps) => {
  const { effective } = useTheme()

  return (
    <Sonner
      theme={effective}
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      // Only `top` is set here, unlike apps/ops's twin (#564): learn has no
      // fixed bottom nav to clear, so bottom stays at sonner's own default
      // on every toast. Object form still matters — sonner fills any side
      // left undefined with its own default, so leaving `bottom` out (not
      // passing `bottom: undefined` explicitly, which sonner would treat the
      // same either way) keeps that default intact and only `top` moves.
      //
      // `top` clears the app header (see --learn-toast-offset-top in
      // globals.css) for the two per-toast `position: "top-center"` toasts in
      // lib/pwa.ts (#623) — every other toast stays at sonner's bottom-right
      // default and never reads it.
      offset={{
        top: "var(--learn-toast-offset-top)",
      }}
      mobileOffset={{
        top: "var(--learn-toast-offset-top)",
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
          // Only lib/pwa.ts's two persistent toasts pass `closeButton: true`,
          // so this class only ever lands on those — sonner's own 20x20px
          // close button (dist/styles.css) is under the 44px mobile tap
          // target minimum (CLAUDE.md Desktop/Mobile Parity). Sizing rule
          // lives in globals.css.
          closeButton: "cn-toast-close-button",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
